import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Table,
  Button,
  Badge,
  Group,
  Text,
  ActionIcon,
  Tooltip,
  Modal,
  Stack,
  Switch,
  TextInput,
  NumberInput,
  LoadingOverlay,
  Box,
} from '@mantine/core';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconArrowLeft,
  IconCheck,
  IconPlayerPlay,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { llmModelsApi } from '@/services/llm-models.api';
import { showError, showSuccess } from '@/utils/errorHandler';
import type { LLMModel, CreateLLMModelRequest, UpdateLLMModelRequest } from '@/types/llm-model.types';

export const LLMModelsPanel = () => {
  const navigate = useNavigate();
  const [models, setModels] = useState<LLMModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<LLMModel | null>(null);
  const [modelToDelete, setModelToDelete] = useState<LLMModel | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    inputPrice: 0,
    cachedInputPrice: 0,
    outputPrice: 0,
    isActive: true,
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    try {
      setLoading(true);
      const data = await llmModelsApi.getAll();
      setModels(data);
    } catch (error) {
      showError(error, 'Erro ao carregar modelos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateModel = async () => {
    if (!formData.name || !formData.displayName) {
      showError(new Error('Preencha todos os campos obrigatórios'));
      return;
    }

    try {
      setSubmitting(true);
      const modelData: CreateLLMModelRequest = {
        name: formData.name,
        displayName: formData.displayName,
        inputPrice: formData.inputPrice,
        cachedInputPrice: formData.cachedInputPrice,
        outputPrice: formData.outputPrice,
        isActive: formData.isActive,
      };

      await llmModelsApi.create(modelData);
      showSuccess('Modelo criado com sucesso');

      setCreateModalOpen(false);
      resetForm();
      loadModels();
    } catch (error) {
      showError(error, 'Erro ao criar modelo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateModel = async () => {
    if (!selectedModel) return;

    if (!formData.name || !formData.displayName) {
      showError(new Error('Nome e nome de exibição são obrigatórios'));
      return;
    }

    try {
      setSubmitting(true);
      const modelData: UpdateLLMModelRequest = {
        name: formData.name,
        displayName: formData.displayName,
        inputPrice: formData.inputPrice,
        cachedInputPrice: formData.cachedInputPrice,
        outputPrice: formData.outputPrice,
        isActive: formData.isActive,
      };

      await llmModelsApi.update(selectedModel.id, modelData);
      showSuccess('Modelo atualizado com sucesso');

      setEditModalOpen(false);
      setSelectedModel(null);
      resetForm();
      loadModels();
    } catch (error) {
      showError(error, 'Erro ao atualizar modelo');
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteModal = (model: LLMModel) => {
    setModelToDelete(model);
    setDeleteModalOpen(true);
  };

  const handleDeleteModel = async () => {
    if (!modelToDelete) return;

    try {
      await llmModelsApi.delete(modelToDelete.id);
      showSuccess('Modelo excluído com sucesso');
      setDeleteModalOpen(false);
      setModelToDelete(null);
      loadModels();
    } catch (error) {
      showError(error, 'Erro ao excluir modelo');
    }
  };

  const handleSelectModel = async (model: LLMModel) => {
    if (model.isSelected) return;

    try {
      setLoading(true);
      await llmModelsApi.select(model.id);
      showSuccess(`Modelo "${model.displayName}" selecionado com sucesso`);
      loadModels();
    } catch (error) {
      showError(error, 'Erro ao selecionar modelo');
      setLoading(false);
    }
  };

  const openEditModal = (model: LLMModel) => {
    setSelectedModel(model);
    setFormData({
      name: model.name,
      displayName: model.displayName,
      inputPrice: typeof model.inputPrice === 'string' ? parseFloat(model.inputPrice) : model.inputPrice,
      cachedInputPrice: typeof model.cachedInputPrice === 'string' ? parseFloat(model.cachedInputPrice) : model.cachedInputPrice,
      outputPrice: typeof model.outputPrice === 'string' ? parseFloat(model.outputPrice) : model.outputPrice,
      isActive: model.isActive,
    });
    setEditModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      displayName: '',
      inputPrice: 0,
      cachedInputPrice: 0,
      outputPrice: 0,
      isActive: true,
    });
  };

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `$${numPrice.toFixed(2)}`;
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Group justify="space-between">
          <Group>
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={() => navigate('/dashboard')}
            >
              <IconArrowLeft size={20} />
            </ActionIcon>
            <Title order={2}>Gerenciamento de Modelos LLM</Title>
          </Group>
          <Button
            leftSection={<IconPlus size={18} />}
            onClick={() => setCreateModalOpen(true)}
          >
            Novo Modelo
          </Button>
        </Group>

        <Paper shadow="sm" p="md" radius="md" pos="relative">
          <LoadingOverlay visible={loading} />

          {models.length === 0 && !loading ? (
            <Box ta="center" py="xl">
              <Text c="dimmed">Nenhum modelo encontrado</Text>
            </Box>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Nome</Table.Th>
                  <Table.Th>Nome de Exibição</Table.Th>
                  <Table.Th ta="center">Input</Table.Th>
                  <Table.Th ta="center">Cache</Table.Th>
                  <Table.Th ta="center">Output</Table.Th>
                  <Table.Th ta="center">Status</Table.Th>
                  <Table.Th ta="center" style={{ width: 140 }}>Ações</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {models.map((model) => (
                  <Table.Tr
                    key={model.id}
                    style={{
                      backgroundColor: model.isSelected ? 'var(--mantine-color-blue-light)' : undefined,
                    }}
                  >
                    <Table.Td>
                      <Group gap="xs">
                        {model.name}
                        {model.isSelected && (
                          <Badge color="blue" size="xs" leftSection={<IconCheck size={10} />}>
                            Em uso
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>{model.displayName}</Table.Td>
                    <Table.Td ta="center">{formatPrice(model.inputPrice)}</Table.Td>
                    <Table.Td ta="center">{formatPrice(model.cachedInputPrice)}</Table.Td>
                    <Table.Td ta="center">{formatPrice(model.outputPrice)}</Table.Td>
                    <Table.Td ta="center">
                      <Badge color={model.isActive ? 'green' : 'gray'} variant="dot">
                        {model.isActive ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="center">
                        <Tooltip label={model.isSelected ? 'Modelo em uso' : !model.isActive ? 'Modelo inativo' : 'Usar este modelo'}>
                          <ActionIcon
                            variant={model.isSelected ? 'filled' : 'subtle'}
                            color="green"
                            onClick={() => handleSelectModel(model)}
                            disabled={model.isSelected || !model.isActive}
                          >
                            {model.isSelected ? <IconCheck size={18} /> : <IconPlayerPlay size={18} />}
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Editar">
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            onClick={() => openEditModal(model)}
                          >
                            <IconEdit size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={model.isSelected ? 'Não pode excluir modelo selecionado' : 'Excluir'}>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => openDeleteModal(model)}
                            disabled={model.isSelected}
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>

        <Text size="sm" c="dimmed">
          Preços por 1 milhão de tokens (USD). O modelo selecionado será usado imediatamente pelo agente em todas as salas.
        </Text>
      </Stack>

      {/* Modal: Criar Modelo */}
      <Modal
        opened={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          resetForm();
        }}
        title="Criar Novo Modelo LLM"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Nome (ID)"
            placeholder="gpt-4o-mini"
            description="Identificador do modelo na API da OpenAI"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <TextInput
            label="Nome de Exibição"
            placeholder="GPT-4o Mini"
            description="Nome amigável para exibição na interface"
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            required
          />

          <NumberInput
            label="Preço Input (por 1M tokens)"
            placeholder="0.25"
            prefix="$"
            decimalScale={4}
            min={0}
            step={0.01}
            value={formData.inputPrice}
            onChange={(value) => setFormData({ ...formData, inputPrice: Number(value) || 0 })}
            required
          />

          <NumberInput
            label="Preço Cached Input (por 1M tokens)"
            placeholder="0.125"
            prefix="$"
            decimalScale={4}
            min={0}
            step={0.01}
            value={formData.cachedInputPrice}
            onChange={(value) => setFormData({ ...formData, cachedInputPrice: Number(value) || 0 })}
            required
          />

          <NumberInput
            label="Preço Output (por 1M tokens)"
            placeholder="1.00"
            prefix="$"
            decimalScale={4}
            min={0}
            step={0.01}
            value={formData.outputPrice}
            onChange={(value) => setFormData({ ...formData, outputPrice: Number(value) || 0 })}
            required
          />

          <Switch
            label="Modelo ativo"
            checked={formData.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
          />

          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setCreateModalOpen(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleCreateModel} loading={submitting}>
              Criar Modelo
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal: Editar Modelo */}
      <Modal
        opened={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedModel(null);
          resetForm();
        }}
        title="Editar Modelo LLM"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Nome (ID)"
            placeholder="gpt-4o-mini"
            description="Identificador do modelo na API da OpenAI"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <TextInput
            label="Nome de Exibição"
            placeholder="GPT-4o Mini"
            description="Nome amigável para exibição na interface"
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            required
          />

          <NumberInput
            label="Preço Input (por 1M tokens)"
            placeholder="0.25"
            prefix="$"
            decimalScale={4}
            min={0}
            step={0.01}
            value={formData.inputPrice}
            onChange={(value) => setFormData({ ...formData, inputPrice: Number(value) || 0 })}
            required
          />

          <NumberInput
            label="Preço Cached Input (por 1M tokens)"
            placeholder="0.125"
            prefix="$"
            decimalScale={4}
            min={0}
            step={0.01}
            value={formData.cachedInputPrice}
            onChange={(value) => setFormData({ ...formData, cachedInputPrice: Number(value) || 0 })}
            required
          />

          <NumberInput
            label="Preço Output (por 1M tokens)"
            placeholder="1.00"
            prefix="$"
            decimalScale={4}
            min={0}
            step={0.01}
            value={formData.outputPrice}
            onChange={(value) => setFormData({ ...formData, outputPrice: Number(value) || 0 })}
            required
          />

          <Switch
            label="Modelo ativo"
            checked={formData.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            disabled={selectedModel?.isSelected && !formData.isActive}
          />
          {selectedModel?.isSelected && (
            <Text size="xs" c="dimmed">
              Não é possível desativar o modelo selecionado
            </Text>
          )}

          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setEditModalOpen(false);
                setSelectedModel(null);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleUpdateModel} loading={submitting}>
              Salvar Alterações
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal: Confirmar Exclusão */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setModelToDelete(null);
        }}
        title="Confirmar Exclusão"
        size="sm"
      >
        <Stack gap="md">
          <Text>
            Tem certeza que deseja excluir o modelo <Text span fw={600}>"{modelToDelete?.displayName}"</Text>?
          </Text>
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => {
                setDeleteModalOpen(false);
                setModelToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button color="red" onClick={handleDeleteModel}>
              Excluir
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};
